import { Leaf, Plug } from 'lucide-react';
import {
  CARBON_EMBODIED_EXPLAINER,
  PASSPORT_ENVIRONMENTAL_PENDING_TEXT,
  type PassportEnvironmental,
} from '@siliconx/shared';
import { UsePhaseCard } from '../UsePhaseCard';
import { Card, CardLabel, EmptyState } from '../shared';

/**
 * SIL-565: Sustainability tab — embodied carbon (Scope 3) + operational
 * emissions (Scope 2). Split out of AssetDetailV2.tsx; content unchanged
 * from the prior inline render (SIL-347/SIL-190 origin).
 *
 * Carbon by GHG Protocol scope. SIL-347's rule — the embodied and use-phase
 * figures are separately reported and must never be summed or netted — was
 * previously carried by physical distance between the two cards on the
 * health tab. Co-locating them here removes that distance, so the rule is
 * now stated in words: a labelled scope band above each card, a rule
 * between them, and the rule spelled out below. Never render a combined
 * total.
 */

const fmtKgV2 = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

/** The Sustainability tab puts the embodied (Scope 3) and use-phase (Scope 2)
 * figures on one panel, so the SIL-347 never-sum rule has to be said out loud
 * rather than implied by distance. Rendered under both cards. */
const SCOPES_NEVER_SUMMED =
  'Scope 3 and Scope 2 are reported separately under the GHG Protocol. They are never summed or netted into a single figure.';

/** Scope band above each carbon card — the label that keeps the two figures
 * legible as separate ledgers rather than two halves of one number. */
function ScopeChip({ scope, children }: { scope: 'scope3' | 'scope2'; children: React.ReactNode }) {
  const Icon = scope === 'scope3' ? Leaf : Plug;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
        scope === 'scope3'
          ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
          : 'border-sky-300 bg-sky-50 text-sky-700'
      }`}
    >
      <Icon className="size-3.5" aria-hidden />
      {children}
    </span>
  );
}

function CarbonCard({ environmental }: { environmental: PassportEnvironmental | null }) {
  const fp = environmental?.carbonFootprint ?? null;
  return (
    <Card>
      <CardLabel>Environmental · embodied carbon</CardLabel>
      <div className="mt-1 text-xs text-zinc-500">{CARBON_EMBODIED_EXPLAINER}</div>
      {fp == null ? (
        <EmptyState
          icon={Leaf}
          message={`${PASSPORT_ENVIRONMENTAL_PENDING_TEXT}.`}
          remedy="Match this asset to a catalog SKU in the Overview tab to compute an embodied-carbon estimate."
        />
      ) : (
        <div className="mt-3.5">
          <div className="flex items-center gap-2">
            <Leaf className="size-4 flex-none text-emerald-600" aria-hidden />
            <span className="font-mono text-xl font-semibold text-zinc-900">
              {fmtKgV2(fp.valueKgco2eq)}
            </span>
            <span className="text-sm text-zinc-500">
              kgCO&#8322;eq · range {fmtKgV2(fp.minKgco2eq)}–{fmtKgV2(fp.maxKgco2eq)}
            </span>
          </div>
          <div className="mt-1.5 font-mono text-[13px]">
            {fp.completeness === 'complete' ? (
              <span className="text-emerald-700">complete · no defaulted inputs</span>
            ) : (
              <span className="text-amber-700">partial · some inputs use archetype defaults</span>
            )}
            <span className="text-zinc-500"> · Boavizta open-source methodology</span>
          </div>
          {/* The disclaimer used to sit in a 360px column beside the figure. It
              wraps to 4-5 lines there while the figure is 2, so the card grew to
              the taller of the two and carried dead space under the number.
              Full-width footnote below instead: same verbatim copy, no
              disclosure hidden behind an affordance, ~half the card height. */}
          {environmental?.disclaimer && (
            <div className="mt-3 border-t border-zinc-100 pt-2.5 text-xs leading-snug text-zinc-400">
              {environmental.disclaimer}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export function SustainabilityTab({
  assetId,
  environmental,
}: {
  assetId: string;
  environmental: PassportEnvironmental | null;
}) {
  return (
    <>
      <div className="flex items-center gap-2">
        <ScopeChip scope="scope3">Scope 3 · Embodied</ScopeChip>
        <span className="text-xs text-zinc-500">One-time, incurred at manufacture.</span>
      </div>
      <CarbonCard environmental={environmental} />

      {/* Plain rule between the two scopes — the never-sum rule is stated
          in full below, so the divider does not need to label itself. */}
      <div className="my-1 h-px bg-zinc-200" />

      <div className="flex items-center gap-2">
        <ScopeChip scope="scope2">Scope 2 · Operational</ScopeChip>
        <span className="text-xs text-zinc-500">
          Recurring, while the asset is in service.
        </span>
      </div>
      <UsePhaseCard assetId={assetId} />

      <p className="text-xs text-zinc-400">{SCOPES_NEVER_SUMMED}</p>
    </>
  );
}
